import React, { useEffect, useState } from "react";
import { AiOutlineArrowLeft, AiOutlineCheck } from "react-icons/ai";
import { GoAlertFill } from "react-icons/go";
import { Form } from "react-bootstrap";
import './portalCreate.css';
import { portalCreate, usersDropDown } from "../../redux/actions/portalAction";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import errorToast from "../../components/toast/errorToast";
import successToast from "../../components/toast/successToast";

const STEP_LABELS = ["Portal Details", "Retention Policy", "Assign Users"];

const PortalCreate = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [priceTier, setPriceTier] = useState("basic");
  const [portalName, setPortalName] = useState("");
  const [retentionPolicy, setRetentionPolicy] = useState("");
  const [usersDropDownData, setUsersDropDownData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isTouched, setIsTouched] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onError = (data) => errorToast(data.data.message);

  useEffect(() => {
    dispatch(usersDropDown({}, (data) => {
      setUsersDropDownData(
        data.data.data.map((item) => ({ label: item.email, value: item.id }))
      );
    }, onError));
  }, []);

  const handleNext = () => {
    if (activeStep === 0 && !portalName.trim()) {
      setIsTouched(true);
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleDone = () => {
    dispatch(portalCreate({
      portal_name: portalName,
      price_tier: priceTier,
      auto_assign: false,
      retention_policy: retentionPolicy,
      users: selectedUsers,
    }, (data) => {
      navigate("/portals");
      successToast(data.data.message);
    }, onError));
  };

  const handleUserSelect = (e) => {
    const { value, checked } = e.target;
    setSelectedUsers((prev) =>
      checked ? [...prev, value] : prev.filter((u) => u !== value)
    );
  };

  const stepContent = [
    /* ── Step 0: Portal Details ──────────────────────────────────── */
    <div key={0} >
      <Form.Group className="mb-2" controlId="formPortalName">
        <Form.Label className="fw_600 fs_14">Portal Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Enter portal name"
          value={portalName}
          onBlur={() => setIsTouched(true)}
          onChange={(e) => setPortalName(e.target.value)}
          isInvalid={isTouched && !portalName.trim()}
        />
        <Form.Control.Feedback type="invalid">
          Portal name is required.
        </Form.Control.Feedback>
      </Form.Group>

      <div className="d-flex align-items-start gap-2 p-3 mb-4 rounded" style={{ background: "#fff8f0", border: "1px solid #fcd9b0" }}>
        <GoAlertFill style={{ color: "#DC6D00", flexShrink: 0, marginTop: 2 }} />
        <p className="mb-0 fs_13 text-start" style={{ color: "#7c4a00" }}>
          Pricing tier cannot be changed after the portal is created.
        </p>
      </div>

      <Form.Group controlId="formPriceTier">
        <Form.Label className="fw_600 fs_14">Price Tier</Form.Label>
        <Form.Select value={priceTier} onChange={(e) => setPriceTier(e.target.value)}>
          <option value="basic">Basic Tier</option>
        </Form.Select>
      </Form.Group>
    </div>,

    /* ── Step 1: Retention Policy ────────────────────────────────── */
    <div key={1}>
      <p className="fs_13 text-muted text-start mb-2">
        Set a retention policy on the portal, which cannot be modified or removed later.
        This overrides any existing project policy. This policy applies to completed jobs only.
        If policy is not set, the retention policy defaults to 90 days.
      </p>
      <Form.Group className="mb-2" controlId="formRetention">
        <Form.Label className="fw_600 fs_14">Retention policy</Form.Label>
        <Form.Control
          type="number"
          placeholder="Enter days (max 90)"
          value={retentionPolicy}
          onChange={(e) => {
            const val = e.target.value;
            if (val <= 90) setRetentionPolicy(val);
            else errorToast("Retention policy must be 90 days or less");
          }}
        />
      </Form.Group>
      <p className="fs_13 text-muted text-start">
        At the end of the retention period, all data associated with the translation job will be
        deleted on the portal. Users are advised to back up their files before the retention period
        expires to avoid any risk of data loss.
      </p>
    </div>,

    /* ── Step 2: Assign Users ─────────────────────────────────────── */
    <div key={2}>
      <p className="fs_13 text-muted text-start mb-2">
        Assign users to this portal (optional). You can also do this later.
      </p>
      {usersDropDownData.length > 0 ? (
        <div className="pc-user-list">
          {usersDropDownData.map((user) => (
            <Form.Check
              key={user.value}
              type="checkbox"
              id={`user-${user.value}`}
              label={user.label}
              value={user.value}
              checked={selectedUsers.includes(user.value)}
              onChange={handleUserSelect}
              className="fs_14"
            />
          ))}
        </div>
      ) : (
        <p className="text-muted fs_13">No users available to assign.</p>
      )}
    </div>,
  ];

  return (
    <div className="w-100 d-flex flex-column" style={{ height: "100%" }}>

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div
        className="d-flex align-items-center border-bottom px-3 position-sticky top-0 bg-white"
        style={{ height: 53, flexShrink: 0 }}
      >
        <AiOutlineArrowLeft
          className="cursor_pointer me-2"
          style={{ color: "#4A81E0", fontSize: 20 }}
          onClick={() => navigate(-1)}
        />
        <span className="fs_18 fw_500">Create New Portal</span>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      <div style={{ overflowY: "auto", flex: 1 }}>
        <div className="pc-stepper-wrap">

          {/* Vertical stepper */}
          <div className="pc-stepper">
            {STEP_LABELS.map((label, index) => (
              <div
                key={index}
                className={`pc-step${activeStep === index ? " active" : ""}${activeStep > index ? " completed" : ""}`}
                onClick={() => activeStep > index && setActiveStep(index)}
              >
                <div className="pc-step-circle">
                  {activeStep > index ? <AiOutlineCheck size={14} /> : index + 1}
                </div>
                <div className="pc-step-label">{label}</div>
              </div>
            ))}
          </div>

          {/* Step content */}
          <div className="pc-content text-start">
            <h6 className="fw_600 fs_18 mb-2 text-start">{STEP_LABELS[activeStep]}</h6>

            {stepContent[activeStep]}

            <div className="d-flex gap-2 mt-4">
              {activeStep === 0 ? (
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={() => navigate("/portals")}
                >
                  Cancel
                </button>
              ) : (
                <button
                  className="btn btn-outline-secondary px-4"
                  onClick={() => setActiveStep((p) => p - 1)}
                >
                  Back
                </button>
              )}
              {activeStep < STEP_LABELS.length - 1 ? (
                <button className="btn btn-primary px-4" onClick={handleNext}>
                  Continue
                </button>
              ) : (
                <button className="btn btn-primary px-4" onClick={handleDone}>
                  Create Portal
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PortalCreate;
